import { useEffect, useState, useCallback } from 'react';
import { mgmt, KcUser, UserDetail, SodViolation } from '../services/api';
import { errMsg, splitList } from '../utils';

const PAGE = 50;

export default function UsersPage() {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<KcUser[]>([]);
  const [total, setTotal] = useState(0);
  const [first, setFirst] = useState(0);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<UserDetail | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await mgmt.listUsers({ search: search || undefined, first, max: PAGE });
      setUsers(r.data.users);
      setTotal(r.data.total);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [search, first]);

  useEffect(() => {
    load();
  }, [load]);

  const openUser = async (id: string) => {
    setErr('');
    try {
      const r = await mgmt.getUser(id);
      setSelected(r.data);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Benutzer</h2>
        <button className="btn-primary" onClick={() => setShowInvite(true)}>
          Benutzer einladen
        </button>
      </div>

      <section className="card space-y-3">
        <div className="flex gap-2">
          <input
            className="input"
            placeholder="Suche (Name, E-Mail, Username)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setFirst(0), load())}
          />
          <button className="btn-ghost" onClick={() => (setFirst(0), load())}>
            Suchen
          </button>
        </div>
        {err && <p className="text-coral">{err}</p>}
        <table className="w-full text-left text-sm">
          <thead className="text-gray-400">
            <tr>
              <th className="py-1">Username</th>
              <th>Name</th>
              <th>E-Mail</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className="cursor-pointer border-t border-edge hover:bg-panel"
                onClick={() => openUser(u.id)}
              >
                <td className="py-1.5 text-white">{u.username}</td>
                <td>{[u.firstName, u.lastName].filter(Boolean).join(' ')}</td>
                <td>{u.email}</td>
                <td>
                  <span className={u.enabled ? 'text-green-400' : 'text-coral'}>
                    {u.enabled ? 'aktiv' : 'deaktiviert'}
                  </span>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={4} className="py-3 text-gray-500">
                  Keine Benutzer gefunden.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>{total} Benutzer</span>
          <div className="flex gap-2">
            <button
              className="btn-ghost"
              disabled={first === 0}
              onClick={() => setFirst(Math.max(0, first - PAGE))}
            >
              Zurück
            </button>
            <button
              className="btn-ghost"
              disabled={first + PAGE >= total}
              onClick={() => setFirst(first + PAGE)}
            >
              Weiter
            </button>
          </div>
        </div>
      </section>

      {selected && (
        <UserDetailPanel detail={selected} onClose={() => setSelected(null)} onChanged={load} />
      )}
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onCreated={() => {
            setShowInvite(false);
            setFirst(0);
            load();
          }}
        />
      )}
    </div>
  );
}

function UserDetailPanel({
  detail,
  onClose,
  onChanged,
}: {
  detail: UserDetail;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { user } = detail;
  const [addRoles, setAddRoles] = useState('');
  const [removeRoles, setRemoveRoles] = useState('');
  const [addGroups, setAddGroups] = useState('');
  const [removeGroups, setRemoveGroups] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const toggleEnabled = async () => {
    setMsg(null);
    try {
      await mgmt.setUserEnabled(user.id, !user.enabled);
      setMsg({ ok: true, text: `Benutzer ${!user.enabled ? 'aktiviert' : 'deaktiviert'}.` });
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
  };

  const applyRoles = async (force = false) => {
    setMsg(null);
    try {
      const r = await mgmt.setUserRoles(user.id, {
        addRoles: splitList(addRoles),
        removeRoles: splitList(removeRoles),
        force,
      });
      const v: SodViolation[] = r.data.sodViolations ?? [];
      if (v.length && !force) {
        const ok = window.confirm(
          `SoD-Verletzung: ${v.map((x) => x.policyName).join(', ')}.\nTrotzdem anwenden (force)?`
        );
        if (ok) return applyRoles(true);
      }
      setMsg({ ok: true, text: 'Rollen aktualisiert.' });
      setAddRoles('');
      setRemoveRoles('');
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
  };

  const applyGroups = async () => {
    setMsg(null);
    try {
      await mgmt.setUserGroups(user.id, {
        addGroups: splitList(addGroups),
        removeGroups: splitList(removeGroups),
      });
      setMsg({ ok: true, text: 'Gruppen aktualisiert.' });
      setAddGroups('');
      setRemoveGroups('');
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: errMsg(e) });
    }
  };

  return (
    <section className="card space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-coral">
          {user.username} {user.email && <span className="text-gray-400">· {user.email}</span>}
        </h3>
        <div className="flex gap-2">
          <button className="btn-ghost" onClick={toggleEnabled}>
            {user.enabled ? 'Deaktivieren' : 'Aktivieren'}
          </button>
          <button className="btn-ghost" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>

      <div>
        <span className="label">Aktive Berechtigungen</span>
        <div className="flex flex-wrap gap-1">
          {detail.entitlements.length === 0 && <span className="text-gray-500">keine</span>}
          {detail.entitlements.map((e) => (
            <span key={`${e.kind}:${e.name}`} className="badge bg-edge text-gray-300">
              {e.kind === 'role' ? '🔑' : '👥'} {e.name}
              <span className="ml-1 text-gray-500">({e.source})</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Rollen hinzufügen (Komma)</label>
          <input className="input" value={addRoles} onChange={(e) => setAddRoles(e.target.value)} />
        </div>
        <div>
          <label className="label">Rollen entfernen (Komma)</label>
          <input
            className="input"
            value={removeRoles}
            onChange={(e) => setRemoveRoles(e.target.value)}
          />
        </div>
      </div>
      <button
        className="btn-primary"
        disabled={!addRoles && !removeRoles}
        onClick={() => applyRoles(false)}
      >
        Rollen anwenden
      </button>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Gruppen hinzufügen (Komma)</label>
          <input
            className="input"
            value={addGroups}
            onChange={(e) => setAddGroups(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Gruppen entfernen (Komma)</label>
          <input
            className="input"
            value={removeGroups}
            onChange={(e) => setRemoveGroups(e.target.value)}
          />
        </div>
      </div>
      <button
        className="btn-primary"
        disabled={!addGroups && !removeGroups}
        onClick={applyGroups}
      >
        Gruppen anwenden
      </button>

      {msg && <p className={msg.ok ? 'text-green-400' : 'text-coral'}>{msg.text}</p>}
    </section>
  );
}

function InviteModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mode, setMode] = useState<'invite' | 'password'>('invite');
  const [password, setPassword] = useState('');
  const [roles, setRoles] = useState('');
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    try {
      await mgmt.createUser({
        username,
        email: email || undefined,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        sendInvite: mode === 'invite',
        temporaryPassword: mode === 'password' ? password : undefined,
        roles: splitList(roles),
      });
      onCreated();
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/60 p-4">
      <div className="card w-full max-w-lg space-y-3">
        <h3 className="font-medium text-coral">Benutzer einladen</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Username</label>
            <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="label">E-Mail</label>
            <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className="label">Vorname</label>
            <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div>
            <label className="label">Nachname</label>
            <input className="input" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label">Initial-Rollen (Komma, optional)</label>
          <input className="input" value={roles} onChange={(e) => setRoles(e.target.value)} />
        </div>
        <div className="flex gap-4 text-sm text-gray-300">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={mode === 'invite'}
              onChange={() => setMode('invite')}
            />
            Einladungs-E-Mail senden
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              checked={mode === 'password'}
              onChange={() => setMode('password')}
            />
            Temporäres Passwort setzen
          </label>
        </div>
        {mode === 'password' && (
          <div>
            <label className="label">Temporäres Passwort</label>
            <input
              className="input"
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
        )}
        {err && <p className="text-coral">{err}</p>}
        <div className="flex justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button
            className="btn-primary"
            disabled={!username || (mode === 'password' && !password)}
            onClick={submit}
          >
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}
